// ...existing imports and code above
// PostMyNeedScreen.js
import React, {useEffect, useMemo, useState} from 'react';
import {
  View,
  Text,
  TextInput,
  Alert,
  StyleSheet,
  Pressable,
  Platform,
  PermissionsAndroid,
  ScrollView,
  KeyboardAvoidingView,
} from 'react-native';
import Geolocation from 'react-native-geolocation-service';
import mystyles from '../../assets/styles/mystyles';
import {callApi, getUserId, requestLocationPermission} from '../Helper/Helper';
import poststyles from '../../assets/styles/poststyles';

// Replace with your actual API base URL or import from your config
const ENDPOINT_URL = 'https://api.eabu.net';

const TABS = {
  FOOD: 'Food',
  HOUSEHOLD: 'Household',
  TRANSPORT: 'Transport',
};

const STEPS = {
  TITLE: 0,
  DETAILS: 1,
  ZIP: 2,
  PHONE: 3,
};

export default function ProviderPostServices() {
  const [activeTab, setActiveTab] = useState(TABS.FOOD);
  const [step, setStep] = useState(STEPS.TITLE);
  const [data, setData] = useState([]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [phone, setPhone] = useState('');
  const [intersted_by, setInterestedBy] = useState(0);
  const [coords, setCoords] = useState(null); // { latitude, longitude }
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState('');
  const [locationError, setLocationError] = useState('');
  const [errors, setErrors] = useState({
    title: '',
    description: '',
    zipcode: '',
    phone: '',
  });

  // Inline "request more" UI state
  const [showRequestMoreForm, setShowRequestMoreForm] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  const [requestMoreText, setRequestMoreText] = useState('');
  const [sendingRequestMore, setSendingRequestMore] = useState(false);

  const [requestMoreMessage, setRequestMoreMessage] = useState('');
  const [requestMoreMessageType, setRequestMoreMessageType] = useState(''); // 'success' | 'error' | ''

  const [userId, setUserId] = useState(null);

  const [eatTabFull, setEatTabFull] = useState(false);
  const [checkingEatTab, setCheckingEatTab] = useState(false);

  const [householdTabFull, setHouseholdTabFull] = useState(false);
  const [checkingHouseholdTab, setCheckingHouseholdTab] = useState(false);

  const [transportTabFull, setTransportTabFull] = useState(false);
  const [checkingTransportTab, setCheckingTransportTab] = useState(false);

  const TITLE_MAX = 25;
  const DESC_MAX = 250;

  /**
   * Calls GET {endPoint}?userId=... (or appends with & if query already exists)
   * and returns the number of items in the response.
   */
  const fetchDataCountByUserId = async (endPoint, userId) => {
    if (!endPoint) throw new Error('endPoint is required');
    if (userId === undefined || userId === null || userId === '')
      throw new Error('userId is required');


    const url = endPoint + "/"+userId;
    try {
      const res = await callApi('GET', url);
      return res.Count;
    } catch (e) {
      console.warn('data check failed', e);
    }
    return 0;
  };
  const tabConfig = useMemo(() => {
    const themeColor = '#0A84FF';

    switch (activeTab) {
      case TABS.FOOD:
        return {
          titleLabel: 'Service Title',
          titlePlaceholder: 'e.g., Fresh meals, Meal prep, Catering',
          descriptionLabel: 'Details',
          descriptionPlaceholder:
            'Examples:\n• Grilled chicken today\n• Vegetarian options\n• Custom meals on request',
          endpoint: '/eabu-eat',
          btnColor: themeColor,
          btnLabel: 'Post Food Service',
        };

      case TABS.HOUSEHOLD:
        return {
          titleLabel: 'Service Title',
          titlePlaceholder: 'e.g., House cleaning, Yard work, Senior care',
          descriptionLabel: 'Details',
          descriptionPlaceholder:
            'Examples:\n• Indoor/outdoor cleaning\n• Weekly yard cleanup\n• Senior care support',
          endpoint: '/eabu-live',
          btnColor: themeColor,
          btnLabel: 'Post Household Service',
        };

      case TABS.TRANSPORT:
        return {
          titleLabel: 'Service Title',
          titlePlaceholder: 'e.g., Rides, Pickup & delivery, Move help',
          descriptionLabel: 'Details',
          descriptionPlaceholder:
            'Examples:\n• Local rides\n• Small item delivery\n• Move-in / move-out help',
          endpoint: '/eabu-move',
          btnColor: themeColor,
          btnLabel: 'Post Transport Service',
        };

      default:
        return {
          titleLabel: 'Service Title',
          titlePlaceholder: '',
          descriptionLabel: 'Details',
          descriptionPlaceholder: '',
          endpoint: '',
          btnColor: '#ccc',
          btnLabel: 'Submit',
        };
    }
  }, [activeTab]);

  const isActiveTabFull =
    (activeTab === TABS.FOOD && eatTabFull) ||
    (activeTab === TABS.HOUSEHOLD && householdTabFull) ||
    (activeTab === TABS.TRANSPORT && transportTabFull);

  const isActiveTabChecking =
    (activeTab === TABS.FOOD && checkingEatTab) ||
    (activeTab === TABS.HOUSEHOLD && checkingHouseholdTab) ||
    (activeTab === TABS.TRANSPORT && checkingTransportTab);

  const getButtonStyle = ({variant, color, disabled}) => {
    if (variant === 'secondary') {
      return {
        paddingVertical: 14,
        paddingHorizontal: 16,
        backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: 8,
        borderWidth: 1,
        borderColor: disabled ? '#E5E7EB' : '#D1D5DB',
      };
    }

    // primary (default)
    return {
      paddingVertical: 14,
      paddingHorizontal: 16,
      backgroundColor: disabled ? '#E5E7EB' : color ?? '#0A84FF',
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: 8,
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 6},
      shadowOpacity: disabled ? 0 : 0.12,
      shadowRadius: 10,
      elevation: disabled ? 0 : 3,
    };
  };

  useEffect(() => {
    (async () => {
      try {
        const id = await getUserId();
        setUserId(id);
      } catch (e) {
        console.warn('Failed to get user id', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!userId) return;

      // pick endpoint + state setters based on tab
      let endPoint = '/provider-my-eat-services';
      let setFull = setEatTabFull;
      let setChecking = setCheckingEatTab;

      if (activeTab === TABS.HOUSEHOLD) {
        endPoint = '/provider-my-live-services';
        setFull = setHouseholdTabFull;
        setChecking = setCheckingHouseholdTab;

      } else if (activeTab === TABS.TRANSPORT) {
        endPoint = '/provider-my-move-services';
        setFull = setTransportTabFull;
        setChecking = setCheckingTransportTab;
      } else {
        // FOOD (or any other default)
        endPoint = '/provider-my-eat-services';
        setFull = setEatTabFull;
        setChecking = setCheckingEatTab;
      }

      setChecking(true);
      try {
        const count = await fetchDataCountByUserId(endPoint, userId);
        const full = count > 0;

        setFull(full);

        if (!full) {
          setShowRequestMoreForm(false);
        }
      } catch (e) {
        console.warn('Failed to check tab count', e);
        setFull(false);
      } finally {
        setChecking(false);
      }
    })();
  }, [activeTab, userId]);

  useEffect(() => {
    // reset fields when switching category
    setStep(STEPS.TITLE);
    setTitle('');
    setDescription('');
    setZipcode('');
    setPhone('');
    setMessage('');
    setErrors({title: '', description: '', zipcode: '', phone: ''});

    // reset inline "request more" form on tab change
    setShowRequestMoreForm(false);
    setSenderEmail('');
    setRequestMoreText('');
    setSendingRequestMore(false);

    setRequestMoreMessage('');
    setRequestMoreMessageType('');
  }, [activeTab]);

  useEffect(() => {
    (async () => {
      const hasPermission = await requestLocationPermission();
      if (!hasPermission) {
        setCoords({latitude: 0, longitude: 0});
        return;
      }
      Geolocation.getCurrentPosition(
        position => {
          const {latitude, longitude} = position.coords;
          setCoords({latitude, longitude});
        },
        error => {
          Alert.alert(
            'Location error',
            locationError || 'Unable to get your location. Defaulting to 0,0.',
          );

          setLocationError(error?.message || 'Unable to get your location. Defaulting to 0,0.');
          setCoords({latitude: 0, longitude: 0});
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 10000,
          forceRequestLocation: true,
          showLocationDialog: true,
        },
      );
    })();
  }, []);

  const handleSendRequestMore = async () => {
    const email = senderEmail.trim();

    setRequestMoreMessage('');
    setRequestMoreMessageType('');

    if (!email) {
      setRequestMoreMessage('Please enter your email address.');
      setRequestMoreMessageType('error');
      return;
    }
    if (!phone.trim()) {
      setRequestMoreMessage('Please enter your contact number.');
      setRequestMoreMessageType('error');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setRequestMoreMessage('Please enter a valid email address.');
      setRequestMoreMessageType('error');
      return;
    }
    if (!requestMoreText.trim()) {
      setRequestMoreMessage('Please enter a message.');
      setRequestMoreMessageType('error');
      return;
    }

    setSendingRequestMore(true);

    const payload = {
      serviceType: activeTab,
      email: email,
      phone: phone.trim(),
      message: requestMoreText.trim(),
      userId: userId,
    };

    try {
      await callApi('POST', '/eabu-service-request', {body: payload});

      setRequestMoreMessage(
        'Request sent. We will contact you by email or phone.',
      );
      setRequestMoreMessageType('success');

      setShowRequestMoreForm(false);
      setRequestMoreText('');
      setSenderEmail('');
    } catch (e) {
      setRequestMoreMessage(
        e?.message || 'Failed to send request. Please try again.',
      );
      setRequestMoreMessageType('error');
    } finally {
      setSendingRequestMore(false);
    }
  };

  const validateStep = s => {
    const next = {title: '', description: '', zipcode: '', phone: ''};

    const t = title.trim();
    const d = description.trim();
    const z = zipcode.trim();
    const p = phone.trim().replace(/[^0-9]/g, '');

    if (
      s === STEPS.TITLE ||
      s === STEPS.DETAILS ||
      s === STEPS.ZIP ||
      s === STEPS.PHONE
    ) {
      if (!t) next.title = 'Please enter a service title.';
      else if (t.length > TITLE_MAX)
        next.title = `Max ${TITLE_MAX} characters.`;
    }

    if (s === STEPS.DETAILS || s === STEPS.ZIP || s === STEPS.PHONE) {
      if (!d) next.description = 'Please add service details.';
      else if (d.length > DESC_MAX)
        next.description = `Max ${DESC_MAX} characters.`;
    }

    if (s === STEPS.ZIP || s === STEPS.PHONE) {
      if (!z) next.zipcode = 'Please enter a ZIP code.';
      else if (z.length !== 5) next.zipcode = 'ZIP code must be 5 digits.';
    }

    if (s === STEPS.PHONE) {
      if (!p) next.phone = 'Please enter a phone number.';
      else if (p.length < 10) next.phone = 'Phone number looks too short.';
    }

    setErrors(next);

    const ok =
      (s === STEPS.TITLE && !next.title) ||
      (s === STEPS.DETAILS && !next.title && !next.description) ||
      (s === STEPS.ZIP && !next.title && !next.description && !next.zipcode) ||
      (s === STEPS.PHONE &&
        !next.title &&
        !next.description &&
        !next.zipcode &&
        !next.phone);

    setMessage(ok ? '' : 'Please fix the highlighted fields.');
    return ok;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep(prev => Math.min(prev + 1, STEPS.PHONE));
  };

  const goBack = () => {
    setMessage('');
    setStep(prev => Math.max(prev - 1, STEPS.TITLE));
  };

  const handleSubmit = async () => {
    if (!validateStep(STEPS.PHONE)) return;

    const lat = coords?.latitude ?? 0;
    const lon = coords?.longitude ?? 0;

    if (lat === 0 || lon === 0) {
      Alert.alert(
        'Location required',
        'Your location was not found, please turn on the location in your Phone',
      );
      return;
    }

    setSubmitting(true);
    try {
      const url = `${ENDPOINT_URL}${tabConfig.endpoint}`;
      const payload = {
        longitude: lon,
        latitude: lat,
        userId: userId,
        zipcode: zipcode.trim(),
        phone: phone.trim(),
        title: title.trim(),
        description: description.trim(),
        interested_by: intersted_by,
      };

      await callApi('POST', url, {body: payload});

      Alert.alert(
        'Posted',
        'All set — your service is live. Check back soon for requests from people nearby.',
        [{text: 'Got it', style: 'default'}],
        {cancelable: true},
      );

      setStep(STEPS.TITLE);
      setTitle('');
      setDescription('');
      setZipcode('');
      setPhone('');
      setMessage('');
      setErrors({title: '', description: '', zipcode: '', phone: ''});
    } catch (e) {
      setMessage(e?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{flex: 1}}>
      {locationError ? (
        <Text style={poststyles.errorText}>{locationError}</Text>
      ) : null}
      <ScrollView
        contentContainerStyle={[{paddingBottom: 24, paddingTop: 0}]}
        keyboardShouldPersistTaps="handled">
        <View style={mystyles.container}>
          <View style={poststyles.tabContainer}>
            <Pressable
              onPress={() => setActiveTab(TABS.FOOD)}
              disabled={checkingEatTab}
              style={({pressed}) => [
                poststyles.tabButton,
                activeTab === TABS.FOOD && poststyles.tabButtonActive,
                pressed && poststyles.tabButtonPressed,
              ]}
              accessibilityRole="tab"
              accessibilityState={{
                selected: activeTab === TABS.FOOD,
                disabled: checkingEatTab,
              }}>
              <Text
                style={[
                  poststyles.tabText,
                  activeTab === TABS.FOOD && poststyles.tabTextActive,
                ]}>
                Food
              </Text>
              <View
                style={[
                  poststyles.tabIndicator,
                  activeTab === TABS.FOOD && poststyles.tabIndicatorActive,
                ]}
              />
            </Pressable>

            <Pressable
              onPress={() => setActiveTab(TABS.HOUSEHOLD)}
              disabled={checkingHouseholdTab}
              style={({pressed}) => [
                poststyles.tabButton,
                activeTab === TABS.HOUSEHOLD && poststyles.tabButtonActive,
                pressed && poststyles.tabButtonPressed,
              ]}
              accessibilityRole="tab"
              accessibilityState={{
                selected: activeTab === TABS.HOUSEHOLD,
                disabled: checkingHouseholdTab,
              }}>
              <Text
                style={[
                  poststyles.tabText,
                  activeTab === TABS.HOUSEHOLD && poststyles.tabTextActive,
                ]}>
                Household
              </Text>
              <View
                style={[
                  poststyles.tabIndicator,
                  activeTab === TABS.HOUSEHOLD && poststyles.tabIndicatorActive,
                ]}
              />
            </Pressable>

            <Pressable
              onPress={() => setActiveTab(TABS.TRANSPORT)}
              disabled={checkingTransportTab}
              style={({pressed}) => [
                poststyles.tabButton,
                activeTab === TABS.TRANSPORT && poststyles.tabButtonActive,
                pressed && poststyles.tabButtonPressed,
              ]}
              accessibilityRole="tab"
              accessibilityState={{
                selected: activeTab === TABS.TRANSPORT,
                disabled: checkingTransportTab,
              }}>
              <Text
                style={[
                  poststyles.tabText,
                  activeTab === TABS.TRANSPORT && poststyles.tabTextActive,
                ]}>
                Transport
              </Text>
              <View
                style={[
                  poststyles.tabIndicator,
                  activeTab === TABS.TRANSPORT && poststyles.tabIndicatorActive,
                ]}
              />
            </Pressable>
          </View>

          <Text style={poststyles.subHeading}>{tabConfig.heading}</Text>

          {isActiveTabChecking ? (
            <View style={{marginTop: 12}}>
              <Text style={poststyles.helperText}>Checking your posts…</Text>
            </View>
          ) : isActiveTabFull ? (
            <View style={{marginTop: 12}}>
              <Text style={poststyles.errorText}>
                You already posted 10 {activeTab} services in last 30 days.{' '}
                <Text
                  onPress={() => setShowRequestMoreForm(v => !v)}
                  style={{textDecorationLine: 'underline'}}>
                  Request more
                </Text>
                .
              </Text>

              {showRequestMoreForm && (
                <View style={{marginTop: 12}}>
                  <Text style={poststyles.label}>Your email</Text>
                  <TextInput
                    value={senderEmail}
                    onChangeText={setSenderEmail}
                    placeholder="name@example.com"
                    keyboardType="email-address"
                    autoCapitalize="none"
                    editable={!sendingRequestMore}
                    style={[poststyles.input, poststyles.inputText]}
                    placeholderTextColor="#6B7280"
                  />

                  <Text style={[poststyles.label, {marginTop: 10}]}>Phone</Text>
                  <TextInput
                    value={phone}
                    onChangeText={setPhone}
                    placeholder="Phone number to contact you"
                    keyboardType="phone-pad"
                    autoCapitalize="none"
                    editable={!sendingRequestMore}
                    style={[poststyles.input, poststyles.inputText]}
                    placeholderTextColor="#6B7280"
                  />

                  <Text style={[poststyles.label, {marginTop: 10}]}>
                    Message
                  </Text>
                  <TextInput
                    value={requestMoreText}
                    onChangeText={setRequestMoreText}
                    placeholder="Tell us why you need more posts…"
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                    editable={!sendingRequestMore}
                    style={[
                      poststyles.input,
                      poststyles.inputText,
                      {minHeight: 120, paddingTop: 12},
                    ]}
                    placeholderTextColor="#6B7280"
                  />

                  <Pressable
                    accessibilityRole="button"
                    onPress={handleSendRequestMore}
                    disabled={sendingRequestMore}
                    style={({pressed}) => [
                      {marginTop: 12},
                      getButtonStyle({
                        variant: 'primary',
                        color: tabConfig.btnColor,
                        disabled: sendingRequestMore,
                      }),
                      pressed &&
                        !sendingRequestMore &&
                        poststyles.navPrimaryPressed,
                    ]}>
                    <Text
                      style={[
                        poststyles.primaryButtonText,
                        sendingRequestMore &&
                          poststyles.buttonTextDisabledOnPrimary,
                      ]}>
                      {sendingRequestMore ? 'Sending…' : 'Send request'}
                    </Text>
                  </Pressable>
                </View>
              )}

              {!!requestMoreMessage && (
                <Text
                  style={[
                    poststyles.errorText,
                    {marginTop: 10},
                    requestMoreMessageType === 'success'
                      ? {color: 'green'}
                      : null,
                  ]}>
                  {requestMoreMessage}
                </Text>
              )}
            </View>
          ) : (
            <View style={{marginTop: 12}}>
              {step === STEPS.TITLE && (
                <>
                  <Text style={poststyles.label}>{tabConfig.titleLabel}</Text>
                  <TextInput
                    value={title}
                    onChangeText={v => {
                      setTitle(v);
                      if (errors.title) setErrors(p => ({...p, title: ''}));
                    }}
                    placeholder={tabConfig.titlePlaceholder}
                    maxLength={TITLE_MAX}
                    editable={!submitting}
                    style={[
                      poststyles.input,
                      poststyles.inputText,
                      errors.title ? poststyles.inputError : null,
                    ]}
                    placeholderTextColor="#6B7280"
                  />
                  <View style={poststyles.helperRow}>
                    <Text style={poststyles.helperText}>
                      {errors.title || 'Keep it short and clear.'}
                    </Text>
                    <Text style={poststyles.counterText}>
                      {title.trim().length}/{TITLE_MAX}
                    </Text>
                  </View>

                  {!!tabConfig.quickTitles?.length && (
                    <View style={poststyles.chipRow}>
                      {tabConfig.quickTitles.map(qt => (
                        <Pressable
                          key={qt}
                          onPress={() => setTitle(qt)}
                          disabled={submitting}
                          style={({pressed}) => [
                            poststyles.chip,
                            pressed && !submitting && poststyles.chipPressed,
                          ]}>
                          <Text style={poststyles.chipText}>{qt}</Text>
                        </Pressable>
                      ))}
                    </View>
                  )}
                </>
              )}

              {step === STEPS.DETAILS && (
                <>
                  <Text style={poststyles.label}>
                    {tabConfig.descriptionLabel}
                  </Text>
                  <TextInput
                    value={description}
                    onChangeText={v => {
                      setDescription(v);
                      if (errors.description)
                        setErrors(p => ({...p, description: ''}));
                    }}
                    placeholder={tabConfig.descriptionPlaceholder}
                    maxLength={DESC_MAX}
                    multiline
                    numberOfLines={6}
                    textAlignVertical="top"
                    editable={!submitting}
                    style={[
                      poststyles.input,
                      poststyles.inputText,
                      {
                        minHeight: 180,
                        paddingTop: 12,
                        paddingBottom: 12,
                        lineHeight: 22,
                      },
                      errors.description ? poststyles.inputError : null,
                    ]}
                    placeholderTextColor="#6B7280"
                  />
                  <View style={poststyles.helperRow}>
                    <Text style={poststyles.helperText}>
                      {errors.description ||
                        'Include what you offer, timing, and pricing if relevant.'}
                    </Text>
                    <Text style={poststyles.counterText}>
                      {description.trim().length}/{DESC_MAX}
                    </Text>
                  </View>
                </>
              )}

              {step === STEPS.ZIP && (
                <>
                  <Text style={poststyles.label}>ZIP code</Text>
                  <TextInput
                    value={zipcode}
                    onChangeText={t => {
                      const v = t.replace(/[^0-9]/g, '').slice(0, 5);
                      setZipcode(v);
                      if (errors.zipcode) setErrors(p => ({...p, zipcode: ''}));
                    }}
                    placeholder="e.g., 94105"
                    keyboardType="number-pad"
                    editable={!submitting}
                    style={[
                      poststyles.input,
                      poststyles.inputText,
                      errors.zipcode ? poststyles.inputError : null,
                    ]}
                    placeholderTextColor="#6B7280"
                  />
                  <Text style={poststyles.helperText}>
                    {errors.zipcode ||
                      'We use this to match you with nearby requests.'}
                  </Text>
                </>
              )}

              {step === STEPS.PHONE && (
                <>
                  <Text style={poststyles.label}>Phone</Text>
                  <TextInput
                    value={phone}
                    onChangeText={t => {
                      const v = t.replace(/[^0-9]/g, '');
                      setPhone(v);
                      if (errors.phone) setErrors(p => ({...p, phone: ''}));
                    }}
                    placeholder="Your contact number"
                    keyboardType="number-pad"
                    editable={!submitting}
                    style={[
                      poststyles.input,
                      poststyles.inputText,
                      errors.phone ? poststyles.inputError : null,
                    ]}
                    placeholderTextColor="#6B7280"
                  />
                  <Text style={poststyles.helperText}>
                    {errors.phone ||
                      'This is shown to people who want to contact you.'}
                  </Text>
                </>
              )}

              {!!message && <Text style={poststyles.errorText}>{message}</Text>}
            </View>
          )}

          <View style={poststyles.navRow}>
            <Pressable
              accessibilityRole="button"
              onPress={goBack}
              disabled={submitting || step === STEPS.TITLE || isActiveTabFull}
              style={({pressed}) => [
                poststyles.navButton,
                getButtonStyle({
                  variant: 'secondary',
                  disabled:
                    submitting || step === STEPS.TITLE || isActiveTabFull,
                }),
                pressed &&
                  !(submitting || step === STEPS.TITLE || isActiveTabFull) &&
                  poststyles.navButtonPressed,
              ]}>
              <Text
                style={[
                  poststyles.navIcon,
                  submitting || step === STEPS.TITLE || isActiveTabFull
                    ? poststyles.navIconDisabled
                    : poststyles.navIconSecondary,
                ]}>
                ←
              </Text>
              <Text
                style={[
                  poststyles.secondaryButtonText,
                  (submitting || step === STEPS.TITLE || isActiveTabFull) &&
                    poststyles.buttonTextDisabled,
                ]}>
                Back
              </Text>
            </Pressable>

            {step < STEPS.PHONE ? (
              <Pressable
                accessibilityRole="button"
                onPress={goNext}
                disabled={submitting || isActiveTabFull}
                style={({pressed}) => [
                  poststyles.navButton,
                  getButtonStyle({
                    variant: 'primary',
                    color: tabConfig.btnColor,
                    disabled: submitting || isActiveTabFull,
                  }),
                  pressed &&
                    !(submitting || isActiveTabFull) &&
                    poststyles.navPrimaryPressed,
                ]}>
                <Text
                  style={[
                    poststyles.primaryButtonText,
                    (submitting || isActiveTabFull) &&
                      poststyles.buttonTextDisabledOnPrimary,
                  ]}>
                  Next
                </Text>
                <Text
                  style={[
                    poststyles.navIcon,
                    poststyles.navIconPrimary,
                    (submitting || isActiveTabFull) &&
                      poststyles.buttonTextDisabledOnPrimary,
                  ]}>
                  →
                </Text>
              </Pressable>
            ) : (
              <Pressable
                accessibilityRole="button"
                onPress={handleSubmit}
                disabled={submitting || isActiveTabFull}
                style={({pressed}) => [
                  poststyles.navButton,
                  getButtonStyle({
                    variant: 'primary',
                    color: tabConfig.btnColor,
                    disabled: submitting || isActiveTabFull,
                  }),
                  pressed &&
                    !(submitting || isActiveTabFull) &&
                    poststyles.navPrimaryPressed,
                ]}>
                <Text
                  style={[
                    poststyles.primaryButtonText,
                    (submitting || isActiveTabFull) &&
                      poststyles.buttonTextDisabledOnPrimary,
                  ]}>
                  {submitting ? 'Posting…' : tabConfig.btnLabel}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
